/*
  Document Mutation Helpers

  Pure functions for immutably updating the journey document.
  These helpers handle graph operations like adding/removing nodes and edges.
*/

import { v4 as uuidv4 } from 'uuid';
import type {
  JourneyDocument,
  Node,
  Edge,
  NodeId,
  AddNodeFormNode,
} from '../types';

/**
 * Adds a node to the document
 *
 * @param doc - Current journey document
 * @param node - Node to add
 * @returns Updated document
 */
export function addNodeToDocument(doc: JourneyDocument, node: Node): JourneyDocument {
  return {
    ...doc,
    nodes: {
      ...doc.nodes,
      [node.id]: node,
    },
  };
}

/**
 * Updates a node in the document
 *
 * @param doc - Current journey document
 * @param nodeId - ID of node to update
 * @param updates - Partial node updates
 * @returns Updated document
 */
export function updateNodeInDocument(
  doc: JourneyDocument,
  nodeId: NodeId,
  updates: Partial<Node>,
): JourneyDocument {
  const existingNode = doc.nodes[nodeId];
  if (!existingNode) return doc;

  return {
    ...doc,
    nodes: {
      ...doc.nodes,
      [nodeId]: {
        ...existingNode,
        ...updates,
        // Merge config if provided
        ...(updates.config && {
          config: {
            ...existingNode.config,
            ...updates.config,
          },
        }),
      } as Node,
    },
  };
}

/**
 * Gets all child nodes (direct targets from outgoing edges)
 *
 * @param doc - Current journey document
 * @param nodeId - Starting node ID
 * @returns Array of child node IDs
 */
function getChildNodes(doc: JourneyDocument, nodeId: NodeId): NodeId[] {
  return Object.values(doc.edges)
    .filter(edge => edge.from.node === nodeId)
    .map(edge => edge.to.node);
}

/**
 * Gets all parent nodes (direct sources from incoming edges)
 *
 * @param doc - Current journey document
 * @param nodeId - Node ID
 * @returns Array of parent node IDs
 */
function getParentNodes(doc: JourneyDocument, nodeId: NodeId): NodeId[] {
  return Object.values(doc.edges)
    .filter(edge => edge.to.node === nodeId)
    .map(edge => edge.from.node);
}

/**
 * Gets all descendant nodes recursively
 *
 * @param doc - Current journey document
 * @param nodeId - Starting node ID
 * @returns Set of descendant node IDs
 */
function getAllDescendants(doc: JourneyDocument, nodeId: NodeId): Set<NodeId> {
  const descendants = new Set<NodeId>();
  const visited = new Set<NodeId>();
  const queue: NodeId[] = [nodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (!visited.has(current)) {
      visited.add(current);

      const children = getChildNodes(doc, current);
      children.forEach(childId => {
        descendants.add(childId);
        queue.push(childId);
      });
    }
  }

  return descendants;
}

/**
 * Deletes nodes from the document while preserving edges with AddNodeForm nodes
 *
 * This matches the old editor's behavior:
 * - Incoming edges are preserved and "capped" with AddNodeForm nodes
 * - Edge labels/conditions (like "Approved", "Denied") are kept
 * - Only orphaned children (with no other parents) are cascade deleted
 *
 * @param doc - Current journey document
 * @param nodeIds - Array of node IDs to delete
 * @param cascade - Whether to cascade delete orphaned children (default: true)
 * @returns Updated document
 */
export function deleteNodesFromDocument(
  doc: JourneyDocument,
  nodeIds: NodeId[],
  cascade = true,
): JourneyDocument {
  const newNodes = { ...doc.nodes };
  const newEdges = { ...doc.edges };

  const nodesToDelete = new Set<NodeId>(nodeIds);

  // For each node being deleted
  nodeIds.forEach(nodeId => {
    const node = doc.nodes[nodeId];
    if (!node) return;

    // Find incoming edges to this node
    const incomingEdges = Object.values(doc.edges).filter(
      edge => edge.to.node === nodeId,
    );

    // Find outgoing edges from this node
    const outgoingEdges = Object.values(doc.edges).filter(
      edge => edge.from.node === nodeId,
    );

    // For each incoming edge, create an AddNodeForm node and redirect the edge
    incomingEdges.forEach(edge => {
      const parentNode = doc.nodes[edge.from.node];
      if (!parentNode) return;

      // Create a new AddNodeForm node
      const addNodeFormId = `addNodeForm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const updatedEdge = {
        ...edge,
        to: {
          ...edge.to,
          node: addNodeFormId,
        },
      };

      const addNodeFormNode: AddNodeFormNode = {
        id: addNodeFormId,
        type: 'addNodeForm',
        name: 'Add new node',
        config: {},
      };

      // Add the new node
      newNodes[addNodeFormId] = addNodeFormNode;

      // Update the edge to point to the AddNodeForm node (preserve label/condition)
      newEdges[edge.id] = updatedEdge;
    });

    // Delete outgoing edges
    outgoingEdges.forEach(edge => {
      delete newEdges[edge.id];
    });

    // If cascade is enabled, find and delete orphaned children
    if (cascade) {
      const children = getChildNodes(doc, nodeId);

      children.forEach(childId => {
        // Check if this child has other parents (besides the deleted node)
        const childParents = getParentNodes(doc, childId);
        const hasOtherParents = childParents.some(
          parentId => parentId !== nodeId && !nodesToDelete.has(parentId),
        );

        // If no other parents, mark for deletion and recursively delete its descendants
        if (!hasOtherParents && !nodesToDelete.has(childId)) {
          nodesToDelete.add(childId);

          // Also delete all descendants of this orphaned child
          const descendants = getAllDescendants(doc, childId);
          descendants.forEach(descendantId => {
            // Only delete if they don't have other parents
            const descendantParents = getParentNodes(doc, descendantId);
            const descendantHasOtherParents = descendantParents.some(
              parentId => !nodesToDelete.has(parentId),
            );
            if (!descendantHasOtherParents) {
              nodesToDelete.add(descendantId);
            }
          });
        }
      });
    }
  });

  // Remove all nodes marked for deletion
  nodesToDelete.forEach(nodeId => {
    delete newNodes[nodeId];
  });

  // Remove edges connected to deleted nodes (except incoming edges we already redirected)
  const finalEdges: Record<string, Edge> = {};
  Object.entries(newEdges).forEach(([edgeId, edge]) => {
    const sourceDeleted = nodesToDelete.has(edge.from.node);
    const targetDeleted = nodesToDelete.has(edge.to.node);

    if (!sourceDeleted && !targetDeleted) {
      finalEdges[edgeId] = edge;
    }
  });

  return {
    ...doc,
    nodes: newNodes,
    edges: finalEdges,
  };
}

/**
 * Adds an edge to the document
 *
 * @param doc - Current journey document
 * @param edge - Edge to add
 * @returns Updated document
 */
export function addEdgeToDocument(doc: JourneyDocument, edge: Edge): JourneyDocument {
  return {
    ...doc,
    edges: {
      ...doc.edges,
      [edge.id]: edge,
    },
  };
}

/**
 * Updates an edge in the document
 *
 * @param doc - Current journey document
 * @param edgeId - ID of edge to update
 * @param updates - Partial edge updates
 * @returns Updated document
 */
export function updateEdgeInDocument(
  doc: JourneyDocument,
  edgeId: string,
  updates: Partial<Edge>,
): JourneyDocument {
  const existingEdge = doc.edges[edgeId];
  if (!existingEdge) return doc;

  return {
    ...doc,
    edges: {
      ...doc.edges,
      [edgeId]: {
        ...existingEdge,
        ...updates,
        ...(updates.config && {
          config: {
            ...existingEdge.config,
            ...updates.config,
          },
        }),
      } as Edge,
    },
  };
}

/**
 * Deletes edges from the document
 *
 * @param doc - Current journey document
 * @param edgeIds - Array of edge IDs to delete
 * @returns Updated document
 */
export function deleteEdgesFromDocument(
  doc: JourneyDocument,
  edgeIds: string[],
): JourneyDocument {
  const newEdges = { ...doc.edges };
  edgeIds.forEach(edgeId => {
    delete newEdges[edgeId];
  });

  return {
    ...doc,
    edges: newEdges,
  };
}

/**
 * Creates a connection between two nodes
 *
 * @param doc - Current journey document
 * @param sourceNodeId - Source node ID
 * @param targetNodeId - Target node ID
 * @param edgeData - Optional edge data (label, config, etc.)
 * @returns Updated document with new edge
 */
export function connectNodes(
  doc: JourneyDocument,
  sourceNodeId: NodeId,
  targetNodeId: NodeId,
  edgeData?: Partial<Edge>,
): JourneyDocument {
  const edgeId = edgeData?.id || `edge-${uuidv4()}`;

  const newEdge: Edge = {
    id: edgeId,
    from: { node: sourceNodeId },
    to: { node: targetNodeId },
    condition: edgeData?.condition || null,
    condition_id: edgeData?.condition_id ?? null,
    config: edgeData?.config || {},
    ...edgeData,
  };

  return addEdgeToDocument(doc, newEdge);
}

/**
 * Reconnects an edge to a new target node
 *
 * @param doc - Current journey document
 * @param edgeId - Edge ID to reconnect
 * @param newTargetNodeId - New target node ID
 * @returns Updated document
 */
export function reconnectEdge(
  doc: JourneyDocument,
  edgeId: string,
  newTargetNodeId: NodeId,
): JourneyDocument {
  const existing = doc.edges[edgeId];
  if (!existing) return doc;

  return updateEdgeInDocument(doc, edgeId, {
    to: { ...existing.to, node: newTargetNodeId },
  });
}

/**
 * Updates branch condition for a start node edge
 *
 * @param doc - Current journey document
 * @param edgeId - Edge ID (branch) to update
 * @param newCondition - New branch condition
 * @returns Updated document
 */
export function updateBranchCondition(
  doc: JourneyDocument,
  edgeId: string,
  newCondition: string,
): JourneyDocument {
  return updateEdgeInDocument(doc, edgeId, {
    condition: newCondition,
  });
}

/**
 * Outcome data structure that can be a string or an object with precedence
 */
export type OutcomeData = string | {
  outcome: string;
  customer_outcome_id?: number | null;
  precedence?: number;
};

/**
 * Generic helper to add a node with automatic child edges for each outcome
 *
 * @param doc - Current journey document
 * @param node - Node to add
 * @param outcomes - Array of outcome labels (strings) or outcome objects with precedence to create as child nodes
 * @param outcomeMap - Optional map from outcome name to customer_outcome_id (for setting condition_id)
 * @returns Updated document with node and child AddNodeForm nodes for each outcome
 */
export function addNodeWithOutcomes(
  doc: JourneyDocument,
  node: Node,
  outcomes: OutcomeData[] = [],
  outcomeMap?: Record<string, number | null>,
): JourneyDocument {
  // First, add the node itself
  let updatedDoc = addNodeToDocument(doc, node);

  // If no outcomes, just return the document with the node added
  if (!outcomes || outcomes.length === 0) {
    return updatedDoc;
  }

  // Create AddNodeForm nodes and edges for each outcome
  outcomes.forEach(outcomeData => {
    const addNodeFormId = `node-${uuidv4()}`;
    const edgeId = `edge-${uuidv4()}`;

    // Extract outcome label and optional precedence from outcome data
    // Support both string format and object format for backward compatibility
    const outcomeName = typeof outcomeData === 'string' ? outcomeData : outcomeData.outcome;
    const precedence = typeof outcomeData === 'string' ? undefined : outcomeData.precedence;
    const customerOutcomeId = typeof outcomeData === 'string'
      ? undefined
      : outcomeData.customer_outcome_id;

    // Look up condition_id from outcome map if available (fallback to embedded ID)
    const conditionId = outcomeMap?.[outcomeName] ?? customerOutcomeId ?? null;

    // Create AddNodeForm node for this outcome
    const addNodeFormNode: AddNodeFormNode = {
      id: addNodeFormId,
      type: 'addNodeForm',
      name: `Add new node for ${outcomeName}`,
      config: {},
    };

    // Create edge from node to AddNodeForm
    // Include precedence from workflow outcome to ensure correct edge ordering
    const edge: Edge = {
      id: edgeId,
      from: { node: node.id },
      to: { node: addNodeFormId },
      condition: outcomeName,
      condition_id: conditionId,
      precedence: precedence !== undefined ? precedence : undefined,
      config: {},
    };

    // Add the node and edge
    updatedDoc = addNodeToDocument(updatedDoc, addNodeFormNode);
    updatedDoc = addEdgeToDocument(updatedDoc, edge);
  });

  return updatedDoc;
}

/**
 * Adds a review node with automatic child edges for outcomes
 * (Approved/Denied for applications, Dismissed/Suspicious for alerts)
 *
 * @param doc - Current journey document
 * @param reviewNode - Review node to add
 * @param outcomes - Array of outcome labels to create as child nodes
 * @param outcomeMap - Optional map from outcome name to customer_outcome_id (for setting condition_id)
 * @returns Updated document with review node and child AddNodeForm nodes for each outcome
 */
export function addReviewNodeWithOutcomes(
  doc: JourneyDocument,
  reviewNode: Node,
  outcomes: string[] = ['Approved', 'Denied'],
  outcomeMap?: Record<string, number | null>,
): JourneyDocument {
  return addNodeWithOutcomes(doc, reviewNode, outcomes, outcomeMap);
}

